import React, { useState, useEffect } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import axios from 'axios';
import Swal from 'sweetalert2';
import GenericModal from './GenericModal';

const CollisionModal = ({ collision, onResolve, onClose, disableUpdate, hubkey }) => {
  const [contactCount, setContactCount] = useState(null);
  const [loadingContactCount, setLoadingContactCount] = useState(false);

  useEffect(() => {
    if (collision?.existingDeal?.id) {
      fetchContactCount(collision.existingDeal.id);
    }
  }, [collision]);

  const fetchContactCount = async (dealId) => {
    if (!dealId) return;
    
    setLoadingContactCount(true);
    try {
      // Use the hubkey prop passed from the parent component
      if (!hubkey) {
        console.error('No HubSpot API key provided to CollisionModal');
        return;
      }
      
      if (hubkey) {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/getDealContactCounts`, {
          hubkey,
          dealIds: [dealId]
        });
        
        const counts = response.data?.dealContactCounts || {};
        setContactCount(counts[dealId] || 0);
      }
    } catch (error) {
      console.error('Error fetching contact count:', error);
      setContactCount(null);
    } finally {
      setLoadingContactCount(false);
    }
  };

  if (!collision) return null;

  const { existingDeal, newDeal, similarity, previewNewTitle, previewNewId } = collision;
  const isSimilarTitle = similarity >= 0.8;

  const handleUpdate = () => {
    if (!disableUpdate) {
      // Show loading modal immediately
      Swal.fire({
        title: 'Updating Project...',
        html: `
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Updating existing project...</p>
            <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      onResolve({ action: 'update', dealId: existingDeal.id });
      onClose();
    }
  };

  const handleCreateNew = () => {
    // Show loading modal immediately
    Swal.fire({
      title: 'Creating New Project...',
      html: `
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Searching for existing projects and creating new one...</p>
          <p class="text-sm text-gray-500 mt-2">This may take a few moments</p>
        </div>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
    
    onResolve({ action: 'create_new' });
    onClose();
  };

  const handleCancel = () => {
    onResolve({ action: 'cancel' });
    onClose();
  };

  const renderContactCountInfo = () => {
    if (loadingContactCount) {
      return (
        <div className="text-xs text-gray-500">
          📧 Loading contact count...
        </div>
      );
    }
    
    if (contactCount === null) {
      return (
        <div className="text-xs text-gray-500">
          📧 Contact count unavailable
        </div>
      );
    }
    
    if (contactCount === 0) {
      return (
        <div className="text-xs text-gray-500">
          📧 No associated contacts
        </div>
      );
    }
    
    return (
      <div className="text-xs">
        <span className="text-blue-600 font-medium">
          📧 {contactCount} associated contact{contactCount !== 1 ? 's' : ''}
        </span>
        {contactCount >= 1000 && (
          <div className="text-red-600 font-bold mt-1">
            ⚠️ HubSpot limit: 1000 contacts per deal
          </div>
        )}
        {contactCount >= 900 && contactCount < 1000 && (
          <div className="text-yellow-600 font-bold mt-1">
            ⚠️ Approaching HubSpot's 1000 contact limit
          </div>
        )}
      </div>
    );
  };

  return (
    <GenericModal isOpen={true} onClose={onClose} draggable dragHandleSelector=".collision-modal-header" maxWidthClass="max-w-4xl">
      {/* Invisible top drag handle with space for the close button on the right */}
      <div className="absolute top-0 left-0 right-10 h-6 collision-modal-header cursor-grab active:cursor-grabbing" />
      <div className="collision-modal-header select-none cursor-grab active:cursor-grabbing mb-3" />
      
      <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div className="sm:flex sm:items-start">
          <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
            <h3 className="text-xl font-bold mb-4">
              Project ID Collision Detected
            </h3>
                  
                  {/* Contact Count Summary */}
                  {contactCount !== null && contactCount > 0 && (
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>📊 Contact Summary:</strong> This existing deal has <strong>{contactCount}</strong> associated contact{contactCount !== 1 ? 's' : ''}
                      </p>
                      {contactCount >= 1000 && (
                        <p className="text-sm text-red-600 mt-1">
                          <strong>⚠️ Warning:</strong> This deal is at HubSpot's 1000 contact limit
                        </p>
                      )}
                      {contactCount >= 900 && contactCount < 1000 && (
                        <p className="text-sm text-yellow-600 mt-1">
                          <strong>⚠️ Caution:</strong> This deal is approaching the 1000 contact limit
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border p-4 rounded-lg bg-gray-50">
                      <h3 className="font-semibold mb-2">Existing Project</h3>
                      <p><span className="font-medium">Title:</span> {existingDeal.title}</p>
                      <p><span className="font-medium">ID:</span> {existingDeal.projectId}</p>
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Description:</span><br/>
                        {existingDeal.description || 'No description available'}
                      </p>
                      <div className="mt-2">
                        {renderContactCountInfo()}
                      </div>
                    </div>
                    <div className="border p-4 rounded-lg bg-gray-50">
                      <h3 className="font-semibold mb-2">New Project (Preview)</h3>
                      <p><span className="font-medium">Title:</span> {previewNewTitle || newDeal.title}</p>
                      <p><span className="font-medium">ID:</span> {previewNewId || newDeal.projectId}</p>
                    </div>
                  </div>
                  {isSimilarTitle && (
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-yellow-800">
                        ⚠️ These appear to be the same project (title similarity: {Math.round(similarity * 100)}%)
                      </p>
                    </div>
                  )}
                  
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Note:</strong> HubSpot limits each deal to a maximum of 1,000 associated contacts. 
                      If you're approaching this limit, consider creating a new deal instead of updating the existing one.
                    </p>
                  </div>
                  <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                    <button
                      onClick={handleUpdate}
                      className={`px-4 py-2 rounded text-white transition-colors ${disableUpdate ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                      disabled={disableUpdate}
                    >
                      Update Existing Project
                    </button>
                    {disableUpdate && (
                      <div className="text-xs text-red-500 mt-2 sm:mt-0 sm:ml-2">
                        Update Existing is disabled because this project ID appears more than once in your import.
                      </div>
                    )}
                    <button
                      onClick={handleCreateNew}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    >
                      Create New Project
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>   {/* closes (3) */}
              </div>     {/* closes (2) */}
            </div>       {/* closes (1) */}
    </GenericModal>
  );
};

export default CollisionModal; 